import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AuthService } from '../../services/auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';
import { AlertService } from '../../services/core/alert.service';
import { GoogleAuthService } from '../../services/OAuth/googleauth.service';

@Component({
  selector: 'app-register',
  standalone: true,
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  animations: [
    trigger('fadeInOut', [
      state('in', style({ opacity: 1 })),
      transition(':enter', [
        style({ opacity: 0 }),
        animate('700ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('700ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  imports: [FormsModule, ReactiveFormsModule, CommonModule, TranslateModule],
})
export class RegisterComponent implements OnInit {
  public dogCount = 0;
  public catCount = 0;
  public otherPetCount = 0;
  public clientType: string = 'PRIVATE';

  registerForm!: FormGroup;
  confirmPass: any;
  petsModal: boolean = false;
  termsAccepted: boolean = true;
  marketingConsentAccepted: boolean = true;

  hidePass = true;
  hideRepeatPass = true;
  pass = 'password';
  repeatPass = 'password';
  token: any;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private route: Router,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private authService: GoogleAuthService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.registerForm = this.fb.group({
      clientType: ['PRIVATE'],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      birthDate: ['', [Validators.required, this.minDateValidator('1850-01-01'), this.minAgeValidator(5)]],
      companyName: [null],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [
        '',
        [Validators.required, this.comprehensivePhoneValidator()],
      ],
      password: ['', [Validators.required, Validators.minLength(6)]],
      newsletter: [false],
      language: ['German'],
      sex: ['', [Validators.required]],
      role: ['USER'],
      stv: [false],
      acceptedTermsAndConditions: [false, [Validators.required]],
      marketingConsent: [false, [Validators.required]],
      referralSource: ['', [Validators.required]],
      otherReferralSource: [null],
      address: this.fb.group({
        street: ['', Validators.required],
        streetNr: ['', Validators.required],
        city: ['', Validators.required],
        zipCode: ['', Validators.required],
        entryCode: ['1234'],
        entranceInstructions: [null],
        additionalInformation: [null],
        dog: [0],
        dogText: [null],
        cat: [0],
        catText: [null],
        otherAnimal: [0],
        otherAnimalText: [null],
        isDefaultAddress: true,
      }),
    });

    this.registerForm.get('referralSource')?.valueChanges.subscribe({
      next: (res) => {
        if (res === 'OTHER' || res === 'EMPFEHLUNG') {
          this.registerForm
            .get('otherReferralSource')
            ?.addValidators([Validators.required]);
        } else {
          this.registerForm
            .get('otherReferralSource')
            ?.removeValidators([Validators.required]);
          this.registerForm.get('otherReferralSource')?.reset();
        }
      },
    });
  }

  minDateValidator(minDate: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;
      return new Date(control.value) >= new Date(minDate)
        ? null
        : { minDate: { min: minDate } };
    };
  }

  minAgeValidator(minAge: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;
      const threshold = new Date();
      threshold.setFullYear(threshold.getFullYear() - minAge);
      return new Date(control.value) <= threshold
        ? null
        : { minAge: { requiredAge: minAge } };
    };
  }

  comprehensivePhoneValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value?.toString().trim();

      if (!value) return null;

      const cleanValue = value.replace(/[\s\-\(\)\+]/g, '');

      if (!/^\d+$/.test(cleanValue)) {
        return {
          invalidPhoneNumber: {
            value: control.value,
            reason: 'Contains invalid characters',
          },
        };
      }

      if (cleanValue.length < 7 || cleanValue.length > 15) {
        return {
          invalidPhoneNumber: {
            value: control.value,
            reason: 'Invalid length',
          },
        };
      }

      const patterns = [
        /^\+[1-9]\d{6,14}$/,
        /^00[1-9]\d{7,13}$/,
        /^0[1-9]\d{6,13}$/,
        /^[1-9]\d{6,14}$/,
      ];

      const originalValue = value;
      const isValidFormat =
        patterns.some((pattern) => pattern.test(cleanValue)) ||
        patterns.some((pattern) => pattern.test(originalValue));

      return isValidFormat
        ? null
        : {
            invalidPhoneNumber: {
              value: control.value,
              reason: 'Invalid format',
            },
          };
    };
  }

  handleUserType(type: any) {
    if (type === 'PRIVATE') {
      this.clientType = type;
      this.registerForm.get('companyName')?.setValue(null);
      this.registerForm.get('clientType')?.setValue(this.clientType);
    } else {
      this.clientType = type;
      this.registerForm.get('clientType')?.setValue(this.clientType);
    }
  }

  setPets(action: string, petType: string) {
    if (action === 'increase') {
      if (petType === 'dog') {
        this.dogCount = this.dogCount + 1;
        this.registerForm.get('address.dog')?.setValue(this.dogCount);
      } else if (petType === 'cat') {
        this.catCount = this.catCount + 1;
        this.registerForm.get('address.cat')?.setValue(this.catCount);
      } else if (petType === 'other') {
        this.otherPetCount = this.otherPetCount + 1;
        this.registerForm
          .get('address.otherAnimal')
          ?.setValue(this.otherPetCount);
      }
    } else if (action === 'decrease') {
      if (petType === 'dog') {
        if (this.dogCount !== 0) {
          this.dogCount = this.dogCount - 1;
          this.registerForm.get('address.dog')?.setValue(this.dogCount);
        }
      } else if (petType === 'cat') {
        if (this.catCount !== 0) {
          this.catCount = this.catCount - 1;
          this.registerForm.get('address.cat')?.setValue(this.catCount);
        }
      } else if (petType === 'other') {
        if (this.otherPetCount !== 0) {
          this.otherPetCount = this.otherPetCount - 1;
          this.registerForm
            .get('address.otherAnimal')
            ?.setValue(this.otherPetCount);
        }
      }
    }
  }

  removeDogs() {
    this.dogCount = 0;
    this.registerForm.get('address.dog')?.setValue(this.dogCount);
    this.registerForm.get('address.dogText')?.setValue(null);
  }

  removeCats() {
    this.catCount = 0;
    this.registerForm.get('address.cat')?.setValue(this.catCount);
    this.registerForm.get('address.catText')?.setValue(null);
  }

  removeOthers() {
    this.otherPetCount = 0;
    this.registerForm.get('address.otherAnimal')?.setValue(this.otherPetCount);
    this.registerForm.get('address.otherAnimalText')?.setValue(null);
  }

  cancelPets() {
    this.petsModal = !this.petsModal;
  }

  toggleModal() {
    this.petsModal = !this.petsModal;
  }

  handleOtherReferralSource(event: any) {
    const value = event.target?.value;
    if (value === 'other') {
      this.registerForm.get('referralSource')?.setValue(value);
    } else {
      this.registerForm.get('referralSource')?.setValue(value);
    }
  }

  /**
   * register function
   */

  register() {
    if (this.registerForm.valid) {
      const pass = this.registerForm.get('password')?.value;
      if (this.checkPass(pass, this.confirmPass)) {
        const value = this.registerForm.get('birthDate')?.value;
        const dateValue = new Date(value);
        const isoString = dateValue.toISOString();
        this.registerForm.get('birthDate')?.setValue(isoString);
        
        // Get hid from query parameter
        const hidParam = this.activatedRoute.snapshot.queryParams['hid'];
        
        // Exclude marketingConsent from request body
        const formValue = { ...this.registerForm.value };
        delete formValue.marketingConsent;
        const formWithoutMarketingConsent = { ...this.registerForm, value: formValue };
        
        this.auth.register(formWithoutMarketingConsent, hidParam).subscribe(
          (res: any) => {
            console.log(res);
            if (res.success) {
              localStorage.setItem('token', res.data.token);
              localStorage.setItem('user', res.data.id);
              // Update authToken$ observable so button updates
              (this.auth as any)._authToken.next(res.data.token);
              this.route.navigate(['profile']);
            }
          },
          (error: any) => {
            console.error(error);
            this.alertService.error(error.error.error, { autoClose: true });
          }
        );
      } else {
        this.alertService.error('Passwörter stimmen nicht überein', {
          autoClose: true,
        });
      }
    } else {
      this.alertService.error(
        'Bitte füllen sie alle erforderlichen felder aus',
        { autoClose: true }
      );
      this.registerForm.markAllAsTouched();
    }
  }

  /**
   *
   * @param pass
   * @param confirmation
   * @returns : boolean
   */

  checkPass(pass: any, confirmation: string): boolean {
    return pass === confirmation;
  }

  handleConfirmPassword(event: any) {
    this.confirmPass = event.target.value;
  }

  toggleTerms() {
    this.termsAccepted = !this.termsAccepted;
    this.registerForm
      .get('acceptedTermsAndConditions')
      ?.setValue(!this.termsAccepted);
    this.cdr.detectChanges();
  }

  toggleMarketingConsent() {
    this.marketingConsentAccepted = !this.marketingConsentAccepted;
    this.registerForm
      .get('marketingConsent')
      ?.setValue(!this.marketingConsentAccepted);
    this.cdr.detectChanges();
  }

  openFile() {
    window.open('assets/terms/AGB_Pebra_Reinigung_Schweiz_AG_2024.pdf');
  }

  toggleVisibility(action: string): void {
    if (action === 'password') {
      this.hidePass = !this.hidePass;
      if (this.hidePass) {
        this.pass = 'password';
      } else {
        this.pass = 'text';
      }
    } else if (action === 'repeatpass') {
      this.hideRepeatPass = !this.hideRepeatPass;
      if (this.hideRepeatPass) {
        this.repeatPass = 'password';
      } else {
        this.repeatPass = 'text';
      }
    }
  }
}
